export interface Company {
  id: string
  company_name: string
  address: string
  account: string
  call_back_date: string
  category: string
  co_reg_no: string
  contact_1: string
  contact_2: string
  contact_3: string
  creation_date: string
  direct_line: string
  direct_line_2: string
  direct_line_3: string
  email_1: string
  email_2: string
  email_3: string
  established: string
  gdpr_compliant: string
  linkedin_1: string
  linkedin_2: string
  linkedin_3: string
  mobile_1: string
  mobile_2: string
  mobile_3: string
  no_longer_trading: string
  notes: string
  position_1: string
  position_2: string
  position_3: string
  principal_activity: string
  salesman: string
  source: string
  tel_no: string
  type_of_contact: string
  unsubscribed: string
  website: string
  wix: string
  // Duplicate metadata
  duplicate_group_id: string | null
  duplicate_confidence: string | null
  duplicate_match_reasons: string | null
  // System
  original_line_number: number | null
  created_at: string
  updated_at: string
  created_by: string | null
  updated_by: string | null
}

export interface CompanyFilters {
  search: string
  category: string
  duplicateStatus: 'all' | 'duplicates' | 'no_duplicates'
  page: number
  pageSize: number
  sortField: string
  sortDir: 'asc' | 'desc'
}

export interface PaginatedResult<T> {
  data: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export const DEFAULT_FILTERS: CompanyFilters = {
  search: '',
  category: '',
  duplicateStatus: 'all',
  page: 1,
  pageSize: 50,
  sortField: 'company_name',
  sortDir: 'asc',
}

// CSV header → DB column mapping
export const CSV_HEADER_MAP: Record<string, string> = {
  'Company Name': 'company_name',
  'Address': 'address',
  'Account': 'account',
  'Call Back Date': 'call_back_date',
  'Category': 'category',
  'Co Reg No': 'co_reg_no',
  'Contact 1': 'contact_1',
  'Contact 2': 'contact_2',
  'Contact 3': 'contact_3',
  'Creation Date': 'creation_date',
  'Direct Line': 'direct_line',
  'Direct Line 2': 'direct_line_2',
  'Direct Line 3': 'direct_line_3',
  'Email 1': 'email_1',
  'Email 2': 'email_2',
  'Email 3': 'email_3',
  'Established': 'established',
  'GDPR Compliant': 'gdpr_compliant',
  'LinkedIn1': 'linkedin_1',
  'LinkedIn2': 'linkedin_2',
  'Linkedin3': 'linkedin_3',
  'Mobile 1': 'mobile_1',
  'Mobile 2': 'mobile_2',
  'Mobile 3': 'mobile_3',
  'No Longer Trading': 'no_longer_trading',
  'Notes': 'notes',
  'Position 1': 'position_1',
  'Position 2': 'position_2',
  'Position 3': 'position_3',
  'Principal Activity': 'principal_activity',
  'Salesman': 'salesman',
  'Source': 'source',
  'Tel No': 'tel_no',
  'Type of Contact': 'type_of_contact',
  'Unsubscribed': 'unsubscribed',
  'Website': 'website',
  'Wix': 'wix',
}

// Columns shown in the table view
export const TABLE_COLUMNS: { key: string; label: string; width?: string }[] = [
  { key: 'company_name', label: 'Company Name', width: '220px' },
  { key: 'category', label: 'Category', width: '140px' },
  { key: 'contact_1', label: 'Contact', width: '150px' },
  { key: 'email_1', label: 'Email', width: '200px' },
  { key: 'tel_no', label: 'Tel No', width: '130px' },
  { key: 'website', label: 'Website', width: '180px' },
  { key: 'type_of_contact', label: 'Type', width: '90px' },
]

// Field groupings for the detail/edit panel
export const FIELD_GROUPS: { label: string; fields: { key: string; label: string; multiline?: boolean }[] }[] = [
  {
    label: 'Company Info',
    fields: [
      { key: 'company_name', label: 'Company Name' },
      { key: 'address', label: 'Address' },
      { key: 'account', label: 'Account' },
      { key: 'category', label: 'Category' },
      { key: 'principal_activity', label: 'Principal Activity' },
      { key: 'co_reg_no', label: 'Co Reg No' },
      { key: 'established', label: 'Established' },
      { key: 'type_of_contact', label: 'Type of Contact' },
      { key: 'source', label: 'Source' },
      { key: 'website', label: 'Website' },
      { key: 'wix', label: 'Wix' },
    ],
  },
  {
    label: 'Contact 1',
    fields: [
      { key: 'contact_1', label: 'Name' },
      { key: 'position_1', label: 'Position' },
      { key: 'email_1', label: 'Email' },
      { key: 'direct_line', label: 'Direct Line' },
      { key: 'mobile_1', label: 'Mobile' },
      { key: 'linkedin_1', label: 'LinkedIn' },
    ],
  },
  {
    label: 'Contact 2',
    fields: [
      { key: 'contact_2', label: 'Name' },
      { key: 'position_2', label: 'Position' },
      { key: 'email_2', label: 'Email' },
      { key: 'direct_line_2', label: 'Direct Line' },
      { key: 'mobile_2', label: 'Mobile' },
      { key: 'linkedin_2', label: 'LinkedIn' },
    ],
  },
  {
    label: 'Contact 3',
    fields: [
      { key: 'contact_3', label: 'Name' },
      { key: 'position_3', label: 'Position' },
      { key: 'email_3', label: 'Email' },
      { key: 'direct_line_3', label: 'Direct Line' },
      { key: 'mobile_3', label: 'Mobile' },
      { key: 'linkedin_3', label: 'LinkedIn' },
    ],
  },
  {
    label: 'Other',
    fields: [
      { key: 'tel_no', label: 'Tel No' },
      { key: 'salesman', label: 'Salesman' },
      { key: 'call_back_date', label: 'Call Back Date' },
      { key: 'creation_date', label: 'Creation Date' },
      { key: 'gdpr_compliant', label: 'GDPR Compliant' },
      { key: 'no_longer_trading', label: 'No Longer Trading' },
      { key: 'unsubscribed', label: 'Unsubscribed' },
      { key: 'notes', label: 'Notes', multiline: true },
    ],
  },
]

export const CONFIDENCE_COLORS: Record<string, { bg: string; text: string }> = {
  definite: { bg: '#e8f5e9', text: '#2e7d32' },
  high: { bg: '#fff3e0', text: '#e65100' },
  medium: { bg: '#fff8e1', text: '#f57f17' },
  low: { bg: '#fce4ec', text: '#c62828' },
}
